import { observable, action } from 'mobx';
import { Helper } from '../../../Helper';
import { IUser } from '../../../interfaces/IUser';
import { IMessage } from '../../../interfaces/IMessage';
import { ILike } from '../../../interfaces/ILike';
import { INode } from '../../../interfaces/INode';
import { IFilterDataMessages } from '../../../interfaces/IFilterDataMessages';
import { IHttpStatus } from '../../../interfaces/IHttpStatus';
import { ITargetingProperties } from '../../../interfaces/ITargetingProperties';
import { ITargetingApplications } from '../../../interfaces/ITargetingApplications';
import { TargetingService } from '../../../services/TargetingService';
import { IApplication, IDefaultValues } from '../../../interfaces/IApplication';
import { IUserRole } from '../../../interfaces/IUserRole';
import CategoryService from '../../../services/CategoryService';
import { IInstance } from '../../../interfaces/IInstance';
import { ITenantSettings } from '../../../interfaces/ITenantSettings';
import TranslationService from '../../../services/TranslationService';
import MessageService from '../../../services/MessageService';
import EventService from '../../../services/EventService';
import BotService from '../../../services/BotService';
import { AppStore } from '../../app/stores';
import { INavigatorContentFromLink } from '../../../interfaces/INavigatorContentFromLink';
import { IFeed } from '../../../interfaces/IFeed';
import { LocalizationService } from '../../../services/LocalizationService';
import { IEnvironment } from '../../../interfaces/IEnvironment';
import UserService from '../../../services/UserService';
import { Environment } from '../../../services/Environment';
import { IMessageResult } from '../../../interfaces/IMessageResult';
import DateTimeService from '../../../services/DateTimeService';
import { LoggingService } from '../../../services/LoggingService';
import { ITemplate } from '../../../interfaces/templates/ITemplate';
import { IFontSlots } from '../../../interfaces/IFontSlots';
import { TargetingV2Service } from '../../../services/TargetingV2Service';
import { ITargetingV2Application } from '../../targetingV2/interfaces/ITargetingApplication';
import { IEventPeriod } from '../../../interfaces/IEventPeriod';
import { IMessageDisplayMode } from '../../../interfaces/IMessageDisplayMode';


export class MessagesStore {

  // Props
  @observable public webpartId?: string;
  @observable public language: "Danish" | "English" | "Swedish" | "German";
  @observable public id: string = Helper.getRandomStringKey();
  @observable public width: number;
  @observable public token: string;
  @observable public tenant: string;
  @observable public tenantSettings: ITenantSettings;
  @observable public isNavigator: boolean;
  @observable public isMessagesTemplateEditor: boolean;
  @observable public openContentFromLink?: INavigatorContentFromLink;
  @observable public hideFullViewCoverOverlay: boolean;
  @observable public hideFullViewDate: boolean;

  @observable public instance: IInstance;
  @observable public description: string;
  @observable public header: boolean;
  @observable public title: string;
  @observable public headLineColor: string;
  @observable public hasDarkBackground: boolean;
  @observable public headlineFontSize: number;
  @observable public headlineFontStyle: string;
  @observable public headlineTextAlign: string;
  @observable public headlineFontWeight: string;
  @observable public inheritFont: boolean;
  @observable public display: IMessageDisplayMode;
  @observable public numMessages: number;
  @observable public backgroundColor: string;
  @observable public overlayColor: string;
  @observable public textColor: string;
  @observable public dateBadgeBackgroundColor: string;
  @observable public dateBadgeTextColor: string;
  @observable public dateBorderColor: string;
  @observable public borderColor: string;
  @observable public borderRadius: number;
  // @observable public categories: ICategory[];
  @observable public selectedCategories: INode[];
  @observable public selectedInstances: INode[];
  @observable public selectedTypesOfContent: INode[];
  @observable public selectedItemStatus: INode[];
  @observable public categoryNodes: INode[];
  @observable public categoryDataItems: any[];
  @observable public shouldFilterByCategory: boolean;
  @observable public maxAmountOfColumns: number;

  @observable public showFilter: boolean;
  @observable public addMessage: boolean;
  @observable public showPaging: boolean;
  @observable public isTargetingAvailable: boolean;
  @observable public isFullPageView: boolean;
  @observable public showCategories: boolean;
  @observable public showImage: boolean;
  @observable public showAuthor: boolean;
  @observable public showTeaser: boolean;
  @observable public showTitle: boolean;
  @observable public showStartDate: boolean;
  @observable public showLikes: boolean;
  @observable public showComments: boolean;
  @observable public showDropShadow: boolean;
  @observable public enableMarkAllAsRead: boolean;
  @observable public showCommandBarItems: boolean;
  @observable public collapseCommandBarItems: boolean;
  @observable public showLocation: boolean;
  @observable public showTimespand: boolean;
  @observable public showUniqueViews: boolean;
  @observable public autoRotate: boolean;
  @observable public autoRotateInterval: number;
  @observable public carouselInterval: number;
  @observable public displayDateOnCover: boolean;
  @observable public cacheTimestamp: any;
  @observable public showShowAllButton?: boolean;
  @observable public showAllButtonLink?: string;
  @observable public isCreatingFromTemplate?: boolean;

  // Pin
  @observable public showPinnedMessages: boolean;
  @observable public onlyShowPinnedMessages: boolean;
  @observable public showSortPinnedPanel: boolean;
  @observable public refreshSortPinnedPanel: boolean;
  @observable public hideSortPanelButton: boolean;
  @observable public messageToUnpin: IMessage;
  @observable public showUnpinPopup: boolean;

  @observable public component: "Messages" | "Event" | "ContentCreator" | "ContentOrganizer" = "Messages";
  @observable public categories: INode[];
  @observable public highlightColor: string;
  @observable public rollupHighlightColor: string;
  @observable public environment: IEnvironment;
  @observable public user: IUser;
  @observable public userRole: "Administrator" | "Editor" | "Reader";
  @observable public source: "Home" | "Mobile" | "SharePoint";
  @observable public applicationSettings: ITargetingApplications;

  @observable public onlyShowIfThereIsContent: boolean;
  @observable public textIfNoContent: string;
  @observable public webpartDisplayMode: number;
  @observable public shouldReQuery: boolean;

  @observable public isMobileApp?: boolean;
  @observable public appStore?: AppStore; // only used in app

  @observable public panelContentIsLoading: boolean;

  @observable public isReviewProcess: boolean;

  @observable public isHidden: boolean;

  @observable public isBotInstalled: boolean;

  @observable public newContentAvailable: boolean;
  @observable public lastRequestForNewContent: Date;

  @action public toggleShowHideComponent(isHidden: boolean): void {
    this.isHidden = isHidden;
    const classList = document.getElementById(this.webpartId)?.classList;
    if (classList) {
      if (isHidden) {
        classList.add('sp-component-hide');
      } else {
        classList.remove('sp-component-hide');
      }
    }
  }

  // Event
  @observable public showPeriod: IEventPeriod;
  @observable public startDate: Date;
  @observable public endDate: Date;

  @observable public isMobile: boolean;
  @observable public preview: boolean;

  @observable public showingContentFromCache: boolean;
  @observable public serverAlert: IHttpStatus;

  @observable public isAdminPage: boolean;


  @observable public hasChanged: string;
  @observable public reloadComponent: boolean;

  @observable public calendarHeight: number | "auto";

  // Link
  @observable public openedFromLink: boolean;

  // Filter
  @observable public availableInstances: any[];
  @observable public readableMessageInstances: IInstance[];
  @observable public readableEventsInstances: IInstance[];
  @observable public readableContentOrganizerInstances: IInstance[];
  @observable public showFilterPanel: boolean;
  @observable public showOuterPanel: boolean;
  @action public hideOuterPanel(): void {
    this.showOuterPanel = false;
    console.log(this.showOuterPanel);
  }

  // Search 
  @observable public showSearch: boolean;
  @observable public searchText = '';
  @observable public isFetchingMessages: boolean;
  @observable public isSearching: boolean;

  // Content
  @observable public messages: IMessage[] = [];
  @observable public filterData: IFilterDataMessages = {
    isFiltered: false,
    hasChanged: false,
    showOnlyAuthorMessages: "All",
    showPeriodMessage: "All time",
    selectedPeolple: [],
    selectedCategories: [],
    startDate: undefined,
    endDate: undefined,
    // Content Creator
    selectedTypesOfContent: [],
    selectedItemStatus: [],
    selectedInstances: [],
  };

  // Selected message
  @observable public messageIndex: number;
  @observable public messageToEdit: IMessage;
  @observable public messageToEditOriginal: IMessage;
  @observable public messageToOpen: IMessage;
  @observable public messageToDelete: IMessage;
  @observable public isSavingMessage: boolean;
  @observable public creatingNewMessage: boolean;
  @observable public messagesOpenedStack: IMessage[] = []; // used to open messages from within messages
  @observable public editOccurrence: boolean;
  @observable public occurrenceSeriesId: number;

  // Popup
  @observable public showCancelEditPopup: boolean;

  // Panel
  @observable public panelIsOpen: boolean;
  @observable public digestPaneIsOpen: boolean;

  // Comment editor open
  @observable public addComment: boolean;

  // Paging
  @observable public pagingInfo: string;
  @observable public pageNumber: number = 1;

  @observable public loading: boolean;
  @observable public loadingCommentsAndLikes: boolean;
  @observable public loadingUniqueViews: boolean;

  // Comments and likes
  @observable public likesEnabled: boolean = undefined;
  @observable public commentsEnabled: boolean = undefined;
  @observable public commentsClicked: boolean = undefined;

  // Notification
  @observable public notification: any;

  // Translations
  @observable public titleTranslated: string = undefined;
  @observable public teaserTranslated: string = undefined;
  @observable public categoriesTranslated: string = undefined;
  @observable public contentTranslated: string = undefined;
  @observable public translationLanguage: string = undefined;
  @observable public isTranslating: boolean = false;

  // Feeds
  @observable public feedOptions: IFeed[];

  // Templates
  @observable public availableTemplates: ITemplate[];

  // User
  @action public setUserRole(userRole: IUserRole): void {
    if (userRole) {
      this.userRole = userRole.title;
      this.user = Helper.syncUserProperties(userRole.user);
    }
  }

  // UserRole
  @action public async getUserRole(): Promise<void> {
    return new Promise((resolve) => {
      UserService.getUserRoleForMessages(Environment.getEnvironmentForMainAPI(this.environment), this.tenant, this.instance?.id, this.component === "Messages" ? "IntraActive-Messages" : "IntraActive-Events", this.isAdminPage).then((result) => {
        if (result.httpStatus.statusCode === 200) {
          this.setUserRole(result.value);
        } else {
          console.log("ERROR: getUserRoleForMessages failed!");
          console.log(result.httpStatus);
        }
        resolve();
      });
    });
  }

  // Targeting
  @observable public targetingProperties: ITargetingProperties[] = [];

  // Teams
  @observable public inTeams: boolean;

  // Categories
  @observable public availableCategoriesForComponent: any[];

  @action public clearTranslation(): void {
    this.titleTranslated = undefined;
    this.teaserTranslated = undefined;
    this.categoriesTranslated = undefined;
    this.contentTranslated = undefined;
    this.isTranslating = false;
  }

  @action public reload(): void {
    this.reloadComponent = true;
    this.hasChanged = Helper.getRandomStringKey();
  }

  // Language

  @action public setLanguage(language: "Danish" | "English" | "Swedish" | "German") {
    this.language = language;
    Helper.setLocalStorage("IA_language", language);
  }

  // Notification

  @action public showNotification(type: "success" | "information" | "warning" | "error", content: string, icon?: string): void {
    this.notification = {
      type: type,
      icon: icon,
      content: content,
      position: -40
    };
    setTimeout(() => {
      this.notification.position = 20;
      setTimeout(() => {
        this.hideNotification();
      }, 5000);
    }, 20);
  }

  @action public hideNotification(): void {
    if (this.notification) {
      this.notification.position = -40;
    }
    setTimeout(() => {
      this.notification = undefined;
    }, 500);
  }

  @action public updateFilter(filter: IFilterDataMessages): void {
    this.filterData = filter;
  }

  // Messages

  @action public async openMessage(message: IMessage, callback?: () => void): Promise<void> {
    if (!this.openedFromLink && message.iaConfiguration?.isNewsLink && message.iaConfiguration?.newsLink) {
      window.open(message.iaConfiguration.newsLink, message.iaConfiguration.openNewsLinkInNewTab ? "_blank" : "_self");
      if (this.component === "Messages" && !message.isRead) {
        MessageService.readMessage(this.environment, this.token, this.tenant, this.instance.instanceName, message.id);
        message.isRead = true;
      }
      if (LoggingService !== undefined) {
        LoggingService.trackEvent(this.component === "Messages" ? "OpenMessage" : "OpenEvent", {
          ActionType: this.component === "Messages" ? "OpenMessage" : "OpenEvent",
          Component: this.component,
          Id: message.id,
          Title: message.title,
          Categories: message.categories,
          StartDate: message.startDate,
          Created: message.created,
          Author: message.author,
          Tenant: this.tenant,
          Instance: this.instance?.instanceName,
          UserId: this.token,
          Environment: this.environment
        });
      }
      return;
    }

    this.panelIsOpen = true;
    this.panelContentIsLoading = true;
    if (!this.preview) {
      if (this.component === "Messages" && !message.isRead) {
        MessageService.readMessage(this.environment, this.token, this.tenant, this.instance.instanceName, message.id);
        message.isRead = true;
      }
      this.messageToOpen = message;
      this.getMessageIndex(message);
      if (message.author && message.author.id) {
        await MessageService.setMessageAuthorOrEditor(this.environment, this.token, this.tenant, this.component, this.instance.instanceName, message, "author");
      }
      if (message.editor && message.editor.id) {
        await MessageService.setMessageAuthorOrEditor(this.environment, this.token, this.tenant, this.component, this.instance.instanceName, message, "editor");
      }
      if (this.cacheTimestamp && new DateTimeService().getDatePlusTwelveHours(new Date(this.cacheTimestamp)) < new Date()) {
        if (this.component === "Event") {
          EventService.getEvent(
            this.environment != undefined ? this.environment : "Production",
            this.token,
            this.tenant,
            this.instance.instanceName,
            message.id
          ).then((messagesResult: IMessageResult) => {
            this.messageToOpen = messagesResult.messages[0];
            this.panelContentIsLoading = false;
          }).catch(() => {
            this.panelContentIsLoading = false;
          });
        } else {
          MessageService.getMessage(
            this.environment != undefined ? this.environment : "Production",
            this.token,
            this.tenant,
            this.instance.instanceName,
            message.id
          ).then((messagesResult: IMessageResult) => {
            this.messageToOpen = messagesResult.messages[0];
            this.panelContentIsLoading = false;
          }).catch(() => {
            this.panelContentIsLoading = false;
          });
        }
      } else {
        this.panelContentIsLoading = false;
      }
      if (this.commentsClicked) {
        var my_element = document.getElementById(`IALikesAndComments_${this.instance.instanceName}_${message.id}_${this.webpartId}`);
        my_element.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest"
        });
        this.commentsClicked = false;
      }
      this.loading = false;
    }
    if (this.appStore) {
      // if we are inside the app, pass this message store to the app store
      // this allows us to programmatically close the opened message
      this.appStore.openedPanelMessageStore = this;
    }
    if (callback) callback();
  }

  @action public editMessage(message: IMessage): void {
    // workaround for copying the data of messageToEdit without keeping the reference
    const temp = JSON.stringify(message);
    this.messageToEdit = Object.assign({}, JSON.parse(temp));
    this.messageToEditOriginal = Object.assign({}, JSON.parse(temp));

    this.getMessageIndex(message);
    this.messageToOpen = undefined;
    setTimeout(() => {
      this.panelIsOpen = true;
    }, 400);
  }

  @action public closeMessage(): void {
    if (!this.isMobileApp) {
      let url = window.location.href;
      if (url.indexOf("?") !== -1) {
        url = window.location.href.split('?')[0];
        const urlParams = new URLSearchParams(window.location.search);
        if (!this.isNavigator && urlParams.get('instance')) {
          url += `?instance=${urlParams.get('instance')}`;
        }
        window.history.pushState("", "", url);
      }
    }
    this.messageToOpen = undefined;
    this.messageToEdit = undefined;
    this.messageToEditOriginal = undefined;
    this.panelIsOpen = false;
    this.openedFromLink = false;
    this.creatingNewMessage = false;
    this.isReviewProcess = false;
  }

  @action public updateMessage(): void {
    this.messages[this.messageIndex] = this.messageToEdit;
    this.messageToOpen = Object.assign({}, this.messageToEdit);
    this.messageToEdit = undefined;
    this.messageToEditOriginal = undefined;
    this.isSavingMessage = false;
    this.hasChanged = Helper.getRandomStringKey();
  }

  @action public async openNextMessage(): Promise<void> {
    this.clearTranslation();
    this.addComment = false;
    await this.openMessage(this.messages[this.messageIndex + 1]);
  }

  @action public async openPreviousMessage(): Promise<void> {
    this.clearTranslation();
    this.addComment = false;
    await this.openMessage(this.messages[this.messageIndex - 1]);
  }

  @action private getMessageIndex(message): void {
    for (let i = 0; i <= this.messages.length - 1; i++) {
      if (message.id === this.messages[i].id) {
        this.messageIndex = i;
        return;
      }
    }
  }

  // Instances

  @observable public hasFetchedMessages: boolean;

  @action public getInstance(ignoreCache?: boolean): Promise<void> {
    return new Promise((resolve) => {
      if (this.instance && this.component !== undefined) {
        this.hasFetchedMessages = true;
        if (this.component === "Messages") {
          MessageService.getInstanceSettings(this.instance.instanceName, this.environment, this.tenant, this.token, this.isAdminPage || ignoreCache).then((result: { httpStatus: IHttpStatus, instance: IInstance }) => {
            if (result.httpStatus.statusCode === 200) {
              if (!result.instance.instanceName || result.instance.instanceName === "") {
                result.instance.instanceName = "Default";
              }
              this.instance = result.instance;
              this.instance.translationEnabled = result.instance.translationEnabled ? result.instance.translationEnabled : false;
              this.instance.pushEnabled = (result.instance.pushEnabled !== undefined && result.instance.pushEnabled !== null) ? result.instance.pushEnabled : false;
              if (!this.instance.settings) {
                this.instance.settings = {};
              }
              if (this.instance?.settings?.enableTeaser) {
                Helper.setLocalStorage("enableTeaser", "enableTeaser");
              } else {
                localStorage.removeItem("enableTeaser");
              }
            } else {
              if (this.serverAlert == undefined) {
                this.serverAlert = {
                  statusCode: result.httpStatus.statusCode,
                  message: new LocalizationService().strings.Messages_ServerErrorGetInstance + this.getServerError(result.httpStatus.statusCode)
                };
              }
              console.log("ERROR: getInstanceSettings failed!");
              console.log(result.httpStatus);
            }
            resolve();
          });
        } else {
          EventService.getInstanceSettings(this.instance.instanceName, this.environment, this.tenant, this.token).then((instance) => {
            if (!instance.instanceName || instance.instanceName === "") {
              instance.instanceName = "Default";
            }
            if (instance) {
              this.instance = instance;
            }
            this.instance.translationEnabled = instance.translationEnabled ? instance.translationEnabled : false;
            resolve();
          });
        }
      } else {
        resolve();
      }
    });
  }

  // Categories

  @action public getCategories(forceUpdate?: boolean): Promise<void> {
    return new Promise((resolve) => {
      if (this.instance && forceUpdate || this.instance && this.categoryNodes === undefined) {
        CategoryService.getCategories(this.environment, this.instance.instanceName, this.tenant, this.token, this.component === "Messages" ? "IntraActive-Messages" : "IntraActive-Events", this.component).then((category) => {
          if (category.length > 0) {
            this.categoryDataItems = category[0].data.items;
            console.log("categoryDataItems");
            console.log(this.categoryDataItems);
            this.categoryNodes = this.getChildCategories(category[0].data.items);
            this.hasChanged = Helper.getRandomStringKey();
            resolve();
          }
        });
      } else {
        resolve();
      }
    });
  }

  @action private getChildCategories(category: any): any[] {
    if (category) {
      const childCategories = [];
      category.forEach((childData: any) => {
        childCategories.push(
          {
            defaultLabel: childData.name,
            id: childData.guid,
            childNodes: this.getChildCategories(childData.children)
          }
        );
      });
      return childCategories;
    } else {
      return undefined;
    }
  }

  // Comments and likes

  @action public likesUpdated(likes: ILike[]): void {
    if (this.messageIndex !== undefined) {
      this.messages[this.messageIndex].likes = likes;
    }
    this.hasChanged = Helper.getRandomStringKey();
  }

  @action public commentsUpdated(comments: number): void {
    this.messages[this.messageIndex].commentCount = comments;
    this.addComment = false;
    this.hasChanged = Helper.getRandomStringKey();
  }

  @action public commentsClick(message: IMessage) {
    this.commentsClicked = true;
    this.openMessage(message);
  }

  @action public updateEditorState(): void {
    this.addComment = !this.addComment;
  }

  // Get Targeting Application information

  @action public async getTargetingApplication(): Promise<void> {
    return await TargetingService.getApplication(this.environment, this.tenant, this.token, this.component === "Messages" ? "Messages" : "Events", this.isAdminPage).then((result) => {
      if (result?.httpStatus?.statusCode === 200) {
        this.applicationSettings = result.value[0];
        let application: IApplication;
        if (result.value.length > 0) {
          const appData = <IApplication>JSON.parse(result.value[0].defaultValues);
          let defaultValues: IDefaultValues;
          if (!appData || Array.isArray(appData)) {
            const list: any[] = [];
            defaultValues = {
              isEnable: false,
              type: "profileData",
              values: list,
              allValues: false
            };
            application = {
              id: undefined,
              title: result.value[0].title,
              name: result.value[0].title,
              instances: [],
              default: defaultValues
            };
          } else {
            application = appData;
            application.default.type = result?.value?.length > 0 && result?.value[0].applicationSettings?.toLocaleLowerCase() === "profile data" ? "profileData" : "userGroups";
          }
          if (this.applicationSettings) {
            this.applicationSettings.application = application;
          }
        }
      } else {
        const localizationService = new LocalizationService();
        if (this.serverAlert == undefined) {
          this.serverAlert = {
            statusCode: result.httpStatus.statusCode,
            message: localizationService.strings.Targeting_ServerError + this.getServerError(result.httpStatus.statusCode)
          };
        }
        console.log("ERROR: getTargetingApplication failed!");
        console.log(result.httpStatus);
      }
    });
  }

  @action public async getTargetingProperties(): Promise<void> {
    TargetingService.getProperties(this.environment !== undefined ? this.environment : "Production", this.tenant, this.token, this.isAdminPage).then((result) => {
      if (result?.httpStatus?.statusCode === 200) {
        this.targetingProperties = result.value;
      } else {
        const localizationService = new LocalizationService();
        if (this.serverAlert == undefined) {
          this.serverAlert = {
            statusCode: result.httpStatus.statusCode,
            message: localizationService.strings.Targeting_ServerError + this.getServerError(result.httpStatus.statusCode)
          };
        }
        console.log("ERROR: getTargetingProperties failed!");
        console.log(result.httpStatus);
      }
    });
  }

  ///////////////// Translation ///////////////

  private readonly translationService: TranslationService = new TranslationService();

  @action public getTranslation(language: string, title: string, teaser: string, text: string, categories?: string): void {
    this.isTranslating = true;
    if (language) {
      const content = [];
      let rawContent = [];
      if (text.length > 4999) {
        let index = 0;
        rawContent = text.split('</p>');
        rawContent.forEach(element => {
          if (content.length === 0) {
            content.push(element);
          } else {
            if ((content[index].length + element.length) <= 4999) {
              content[index] += element;
            } else {
              content.push(element);
              index++;
            }
          }
        });

      } else {
        content.push(text);
      }
      const translatedContent = content;
      for (let index = 0; index < content.length; index++) {
        const phrase = content[index];
        if (phrase.length > 3) {
          this.translationService.getTranslations(this.environment, this.token, this.tenant, title, categories, phrase, teaser, language).then((translation) => {
            title = translation[0];
            categories = translation[1] !== '' ? translation[1] : undefined;
            text = translation[2];
            teaser = translation[3];
            this.titleTranslated = title;
            this.teaserTranslated = teaser;
            this.categoriesTranslated = categories;
            translatedContent[index] = text;
            this.contentTranslated = translatedContent.toString();
            this.isTranslating = false;
          });
        }
      }
    } else {
      this.clearTranslation();
    }
  }

  // get Bot status
  @action public getBotInstallationStatus(forceUpdate?: boolean): Promise<void> {
    return new Promise((resolve) => {
      if ((forceUpdate || this.isBotInstalled === undefined) && this.environment && this.tenant) {
        BotService.getBotInstallationStatus(this.environment, this.tenant).then(botStatus => {
          this.isBotInstalled = botStatus.isInstalled;
          resolve();
        });
      } else {
        resolve();
      }
    });
  }

  // get Bot status
  @action public getNewBotInstallationStatus(forceUpdate?: boolean): Promise<void> {
    return new Promise((resolve) => {
      if ((forceUpdate || this.isBotInstalled === undefined) && this.environment && this.tenant) {
        BotService.getNewBotInstallationStatus(this.environment, this.tenant).then(botStatus => {
          this.isBotInstalled = botStatus.isInstalled;
          resolve();
        });
      } else {
        resolve();
      }
    });
  }

  @action public getServerError(statusCode: number): string {
    const localizationService = new LocalizationService();
    switch (statusCode) {
      case 401: // Unauthorized
        return localizationService.strings.Messages_ServerError401;
      case 403: // Forbidden
        return localizationService.strings.Messages_ServerError403;
      case 500: // Internal Server Error
        return localizationService.strings.Messages_ServerError500;
      case 503: // Service Unavailable
        return localizationService.strings.Messages_ServerError503;
      default: // Bad Request
        return localizationService.strings.Messages_ServerError400;
    }
  }

  @observable public showConvertToComposerPopup: IMessage;

  @action public sortMessageBySortOrder(messagesToSort: IMessage[], sortOrderIDs: number[]): IMessage[] {
    const messagesToSortCopy = [...messagesToSort];
    const sortedList: IMessage[] = [];
    sortOrderIDs.forEach(id => {
      const messageId = messagesToSortCopy.findIndex(message => message.id === id);
      if (messageId !== -1) {
        sortedList.push(messagesToSortCopy[messageId]);
        messagesToSortCopy.splice(messageId, 1);
      }
    });
    messagesToSortCopy.push(...sortedList);
    return messagesToSortCopy;
  }

  @action public getMessageEndDate(): Date {
    let endDate: Date = undefined;
    if (this.instance?.settings?.shouldByDefaultExpireAfterAmountOfDays) {
      const now = new Date();
      now.setDate(now.getDate() + this.instance.settings.shouldByDefaultExpireAfterAmountOfDays);
      endDate = now;
    }
    if (!endDate && this.component === "Event") {
      endDate = new DateTimeService().getDatePlusOneHour(new Date())
    }
    return endDate;
  }

  @action public async setTemplateProperties(template: IMessage, defaultEndDate: Date): Promise<IMessage> {
    // setting dates
    template.startDate = new Date();
    template.endDate = defaultEndDate;

    // remove pin if user is not admin
    if (this.userRole !== "Administrator" && template.isPinned) {
      template.isPinned = false;
    }

    // setting pinned date if pinned - default to 7 days in the future
    if (template.isPinned) {
      template.pinnedEndDate = new Date(new Date().setDate(new Date().getDate() + 7));
    }

    // there is an edge case, where some targeting properties has been set on a template, but deleted on the application itself (from within Admin)
    // there is no sync process, so we check manually here, and add those who are still enabled on the application - only working when using groups (not profile data)
    if (template.iaTargetingGroupData) {
      let component: ITargetingV2Application;
      if (this.component === "Messages") component = "messages";
      if (this.component === "Event") component = "events";
      try {
        const groups = (await TargetingV2Service.getTargetingGroups(this.environment, this.tenant, component, this.instance?.instanceName ?? "Default"))?.groupEntries;
        if (groups?.length > 0) {
          let iaTargetingGroupData = "";
          groups.forEach(g => {
            if (template.iaTargetingGroupData.includes(`;${g.id};`)) {
              iaTargetingGroupData += `;${g.id};`;
            }
          });
          template.iaTargetingGroupData = iaTargetingGroupData;
        }
      } catch (e) {
        if (e === 501) {
          console.error("This message instance is using profile data for targeting", e);
        }
      }
    }
    return template;
  }

  // Font space
  @observable public fontSlots: IFontSlots;

  @observable public userLocation: string;

  // Paging on calendar events
  @action public getCalendarPage(dateTimeService: DateTimeService, pageDirection: "next" | "prev"): void {
    const sign = pageDirection === "next" ? 1 : -1;
    switch (this.showPeriod) {
      case 'day':
        let nextOrPrevDate = new Date(this.startDate);
        nextOrPrevDate.setDate(nextOrPrevDate.getDate() + (1 * sign));
        this.startDate = dateTimeService.getDateAtMidnight(nextOrPrevDate);
        this.endDate = dateTimeService.getDateJustBeforeMidnight(
          nextOrPrevDate
        );
        break;

      case 'week':
        let nextOrPrevWeekStartDate = new Date(this.startDate);
        nextOrPrevWeekStartDate.setDate(nextOrPrevWeekStartDate.getDate() + (7 * sign));
        this.startDate = nextOrPrevWeekStartDate;
        let endDate = new Date(this.startDate);
        endDate.setDate(endDate.getDate() + 6);
        this.endDate = endDate;
        break;

      case 'month':
        let nextOrPrevMonthStartDate = new Date(this.startDate);
        nextOrPrevMonthStartDate.setFullYear(
          nextOrPrevMonthStartDate.getFullYear(),
          nextOrPrevMonthStartDate.getMonth() + (1 * sign),
          1
        );
        this.startDate = nextOrPrevMonthStartDate;
        let lastDay = new Date(
          nextOrPrevMonthStartDate.getFullYear(),
          nextOrPrevMonthStartDate.getMonth() + 1,
          0
        );
        this.endDate = lastDay;
        break;
    }
  }

  @action public getCalendarDatePage(dateTimeService: DateTimeService, date: Date): void {
    switch (this.showPeriod) {
      case 'day':
        this.startDate = dateTimeService.getDateAtMidnight(date);
        this.endDate = dateTimeService.getDateJustBeforeMidnight(date);
        break;

      case 'week':
        const startOfWeek = dateTimeService.getStartOfWeek(date);
        const endOfWeek = dateTimeService.getEndOfWeek(date);
        this.startDate = dateTimeService.getDateAtMidnight(startOfWeek);
        this.endDate = dateTimeService.getDateJustBeforeMidnight(endOfWeek);
        break;

      case 'month':
        const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        this.startDate = dateTimeService.getDateAtMidnight(startOfMonth);
        this.endDate = dateTimeService.getDateJustBeforeMidnight(endOfMonth);
        break;
    }
  }
}

